// Angular-Module
import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
// ReactiveX for JavaScript
import {Observable} from 'rxjs';
// Globale Services einbinden
import {AuthService} from './../../services/auth.service';
// Interfaces für Structured Objects einbinden
import {CWResult} from '@shared/cw-result';
// Environment einbinden
import {environment} from '@environment';
import {hasOwn} from '@shared/utils';
import {ActivatedRoute} from '@angular/router';
import {BackendService} from '@global/services/backend.service';
import {TokenStorageService} from '@global/services/token-storage.service';

@Component({
    selector: 'phscw-global-login',
    templateUrl: './global-login.component.html',
    styleUrls: ['./global-login.component.scss'],
    standalone: false
})
export class GlobalLoginComponent implements OnInit, OnDestroy {
    // Flag definiert, ob es beim Login zu einem Fehler kam
    public loginError = false;
    // Flag definiert, welcher Fehler ausgegeben wird
    public loginErrorTranslation = 'UNAVAILABLE';
    // Flag definiert, ob Loginanfrage gerade läuft
    public loading = false;
    private impersonateUsername = null;

    // Link für "Passwort vergessen"
    passwordForgotLink = '';

    public ssoConfig = [];

    whitespaceInUsername = false;

    showPassword = false;

    form = this.fb.group({
        username: ['', Validators.required],
        password: ['', Validators.required],
    });

    get username() {
        return this.form.get('username') as FormControl;
    }

    get password() {
        return this.form.get('password') as FormControl;
    }

    /**
     * Konstruktor (inkl. dependency injection)
     * @param {AuthService} authService - AuthService
     * @param {ActivatedRoute} route - ActivatedRoute
     * @param {BackendService} backendService - BackendService
     * @param {TokenStorageService} tokenStorageService - TokenStorageService
     * @param {FormBuilder} fb - FormBuilder
     */
    constructor(
        private authService: AuthService,
        private route: ActivatedRoute,
        private backendService: BackendService,
        private tokenStorageService: TokenStorageService,
        private fb: FormBuilder,
    ) {}

    /**
     * Initialisieren
     */
    ngOnInit() {
        this.username.valueChanges.subscribe(() => {
            // check if it has whitespaces
            if (this.username.value.includes(' ')) {
                this.whitespaceInUsername = true;
                this.loginErrorTranslation = 'WHITESPACEINUSERNAME';
            } else {
                this.whitespaceInUsername = false;
            }
        });

        // wenn ein tokem im storage ist, redirect ins system
        if (this.tokenStorageService.token) {
            this.authService.redirectOnSuccess(false);
        }
        // generelle Konfig laden
        this.checkSSOConfig();

        // Setze passwordForgotLink über Environment-Variable. Falls nicht vorhanden, erscheint kein Link "Passwort vergessen"
        if (typeof environment.passwordForgotLink !== 'undefined') {
            this.passwordForgotLink = environment.passwordForgotLink;
        }

        // falls der redirect von microsoft zurückkommt
        this.checkSSO();

        // username aus routen ziehen, falls gesetzt
        this.route.queryParamMap.subscribe((params) => {
            this.impersonateUsername = params.get('username');
        });
    }

    /**
     * Aufräumen
     */
    ngOnDestroy() {}

    // eslint-disable-next-line jsdoc/require-returns
    /**
     * @description   Submit des Login-Formulars.
     * Login-Prozess läuft über authService.
     * @author  Massimo Feth <m.feth@pharmakon.software>
     */
    clickLogin(): void {
        // Credentials aus Formular
        const loginCredentials = {
            username: this.username.value,
            password: this.password.value,
        };

        if (this.impersonateUsername !== null) {
            loginCredentials['impersonateUsername'] = this.impersonateUsername;
        }
        // Falls Username und / oder Passwort fehlen wird abgebrochen
        if (loginCredentials.username === '' || loginCredentials.password === '') {
            return;
        }
        // Flag setzen --> Loginanfrage läuft nun
        this.loading = true;
        // Bei neuer Loginanfrage wird loginError zurück gesetzt
        this.loginError = false;
        // Login über authService aufrufen
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const authServiceLogin$: Observable<any> = this.authService.login(loginCredentials);
        authServiceLogin$.subscribe(
            // onNext
            (result: CWResult) => {
                // Login fehlgeschlagen?
                if (!result.success) {
                    // Nachricht setzen
                    this.setErrorMessage(result.data);

                    // Flag setzen
                    this.loginError = true;
                }
                this.loading = false;
            },
            // onError
            () => {
                // Keine Antwort vom Backend erhalten (Servererror)
                this.loginError = true;
                this.loading = false;
            },
        );
    }

    /**
     * @description   Weiter leiten zur Microsoft Login seite
     * @author  Nick Mühlberger <n.muehlberger@pharmakon.software> Eric Haeussel <e.haeusel@pharmakon.software>
     */
    clickLoginMicrosoft() {
        const clientId = this.ssoConfig['client_id'];
        const tenantId = this.ssoConfig['tenant_id'];
        const redirectUri = encodeURIComponent(this.ssoConfig['redirect_uri']);
        const scope = 'openid profile email phone address';
        const responseType = 'code';

        /*
         * wird benutzt um csrf attacken zu verhindern src:
         * https://learn.microsoft.com/en-us/aspnet/core/security/anti-request-forgery?view=aspnetcore-8.0
         */
        const state = this.generateRandomString(100);
        localStorage.setItem('state', state);

        const baseUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize`;
        const queryParams = [
            `client_id=${clientId}`,
            `redirect_uri=${redirectUri}`,
            `response_type=${responseType}`,
            `scope=${encodeURIComponent(scope)}`,
            `state=${state}`,
        ].join('&');

        const url = `${baseUrl}?${queryParams}`;
        window.location.href = url;
    }

    /**
     * @param {any} resultData -
     * @description   Prüft geladene Daten und setzt Fehlermeldung entsprechend
     * @author  Tobias Hannemann <t.hannemann@pharmakon.software>
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setErrorMessage(resultData: any): void {
        if (hasOwn(resultData, 'logins_failed') && resultData.logins_failed === true) {
            this.loginErrorTranslation = 'MAXLOGINSFAILED';
        } else if (hasOwn(resultData, 'deleted') && resultData.deleted === true) {
            this.loginErrorTranslation = 'DELETED';
        } else if (hasOwn(resultData, 'not_found') && resultData.not_found === true) {
            this.loginErrorTranslation = 'NOTFOUND';
        } else if (hasOwn(resultData, 'incomplete_configuration') && resultData.incomplete_configuration === true) {
            this.loginErrorTranslation = 'INCOMPLETECONFIGURATION';
        } else if (hasOwn(resultData, 'mismatch') && resultData.mismatch === true) {
            this.loginErrorTranslation = 'MISMATCH';
        } else {
            this.loginErrorTranslation = 'UNAVAILABLE';
        }
    }

    /**
     * @param {string} length - Länge des zu generierenden Strings
     * @description   Erzeugt random string für state
     * @author  Eric Haeussel <e.haeusel@pharmakon.software>
     * @returns {string} random string
     */
    private generateRandomString(length: number): string {
        const array = new Uint32Array(length);
        window.crypto.getRandomValues(array);
        return Array.from(array, (x) => ('00' + x.toString(16)).slice(-2)).join('').substring(0, length);
    }

    /**
     * @description   ausgelagerte Method um automatisch Weiterzuleiten wenn von SSo Login erfolgreich war
     * @author  Eric Haeussel <e.haeusel@pharmakon.software>
     */
    private checkSSO() {
        // redirect through sso login
        if (this.route.snapshot.url[0].path === 'sso') {
            this.loading = true;
            // eslint-disable-next-line consistent-return
            this.route.queryParams.subscribe((params) => {
                const code = params['code'];
                /*
                 * wird benutzt um csrf attacken zu verhindern src:
                 * https://learn.microsoft.com/en-us/aspnet/core/security/anti-request-forgery?view=aspnetcore-8.0
                 */
                if (params['state'] !== localStorage.getItem('state')) {
                    // eslint-disable-next-line no-console
                    console.error('state inconsistent - potential CSRF attack');
                    return false;
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const authServiceLogin$: Observable<any> = this.authService.ssoLogin(code);
                authServiceLogin$.subscribe(
                    // onNext
                    (result: CWResult) => {
                        // Login fehlgeschlagen?
                        if (!result.success) {
                            // Nachricht setzen
                            this.setErrorMessage(result.data);
                            // Flag setzen
                            this.loginError = true;
                        }
                        this.loading = false;
                    },
                    // onError
                    () => {
                        // Keine Antwort vom Backend erhalten (Servererror)
                        this.loginError = true;
                        this.loading = false;
                    },
                );
            });
        }
    }

    /**
     * @description   Lädt die endpunkte für SSO, endpunkt geht ohne Login da keine sicherheitsrelevanten infos vorhanden
     * @author  Eric Haeussel <e.haeusel@pharmakon.software>
     */
    private checkSSOConfig() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const authServiceLogin$: Observable<any> = this.backendService.getRequest('Config/getSSOConfig');
        authServiceLogin$.subscribe(
            // onNext
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (result: any) => {
                this.ssoConfig = result.data;
            },
            // onError
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (error: any) => {
                // Keine Antwort vom Backend erhalten (Servererror)

                // eslint-disable-next-line no-console
                console.error('ConfigEndpunkt');
                // eslint-disable-next-line no-console
                console.error(error);
            },
        );
    }
}
